import type { Registry } from '../content/registry';
import type { NameList } from '../content/types';
import { hash, hash01, Rng } from './rng';

/** Settlement grid cell size in tiles. Each cell holds at most one town. */
export const CELL = 1024;

export interface Settlement {
  cx: number; cy: number;     // cell
  x: number; y: number;       // centre (tiles)
  radius: number;             // where suburbs end
  core: number;               // downtown radius
  name: string;
  size: 'village' | 'town' | 'city';
  /** architectural style tag (e.g. style_brick) boosting matching buildings; '' = none */
  style: string;
}

/** how far a rail leg runs past its end: through the town and 96 tiles beyond its rim (whole segments), or one segment past the corner */
export function railEnd(town: Settlement | null): number { return town ? Math.ceil((town.radius + 96) / 128) * 128 : 128; }
export interface Corridor { axis: 'v' | 'h'; pos: number; from: number; to: number; /** towns at the low and high end */ lo: Settlement; hi: Settlement; /** the town this leg of the L ends in at its from / to end (null: the corner where the other leg takes over) */ ends: [Settlement | null, Settlement | null] }

/**
 * Macro structure: towns on a jittered grid, separated by wilderness, joined by
 * highway corridors (an L per neighbouring pair). Everything hashed from the seed.
 */
export class Settlements {
  private cache = new Map<string, Settlement | null>();
  private corridorCache = new Map<string, Corridor[]>();
  private lists: NameList[];
  private styles: string[];

  constructor(readonly seed: number, reg: Registry) {
    this.lists = reg.all('namelist').filter((n) => n.kind === 'town' && (n.weight ?? 1) > 0);
    const st = new Set<string>();
    for (const b of reg.all('building')) for (const t of b.tags) if (t.startsWith('style_')) st.add(t);
    this.styles = [...st].sort();
  }

  /** The settlement of a grid cell, if any (with a unique-ish name). */
  at(cx: number, cy: number): Settlement | null {
    const k = `${cx},${cy}`;
    const c = this.cache.get(k);
    if (c !== undefined) return c;
    const raw = this.raw(cx, cy);
    let s: Settlement | null = null;
    if (raw) {
      s = { ...raw };
      // disambiguate against lexicographically lower neighbours: 'Brightwater' → 'Brightwater II'
      let n = 1;
      for (let j = -2; j <= 2; j++) for (let i = -2; i <= 2; i++) {
        if (j > 0 || (j === 0 && i >= 0)) continue;
        const o = this.raw(cx + i, cy + j);
        if (o && o.name === raw.name) n++;
      }
      if (n > 1) s.name = `${raw.name} ${['', 'II', 'III', 'IV', 'V'][Math.min(4, n - 1)] || n}`;
    }
    this.cache.set(k, s);
    return s;
  }

  private rawCache = new Map<string, Settlement | null>();
  /** Settlement before name disambiguation: a pure function of the cell. */
  private raw(cx: number, cy: number): Settlement | null {
    const k = `${cx},${cy}`;
    const c = this.rawCache.get(k);
    if (c !== undefined) return c;
    const rng = new Rng(hash(this.seed, cx, cy, 8081));
    let s: Settlement | null = null;
    // origin cell always has a town so new players start somewhere; elsewhere 70 %
    if ((cx === 0 && cy === 0) || rng.chance(0.7)) {
      const r = rng.float();
      const size: Settlement['size'] = cx === 0 && cy === 0 ? 'town' : r < 0.25 ? 'village' : r < 0.75 ? 'town' : 'city';
      const radius = size === 'village' ? 140 + rng.float() * 60 : size === 'town' ? 260 + rng.float() * 120 : 420 + rng.float() * 160;
      const jitter = CELL / 2 - radius - 64; // keep the whole town inside its cell
      // the origin town sits on the origin so a new world starts in a street, not in a forest
      const x = cx === 0 && cy === 0 ? 96 : cx * CELL + CELL / 2 + Math.round((rng.float() - 0.5) * 2 * Math.max(0, jitter));
      const y = cx === 0 && cy === 0 ? 96 : cy * CELL + CELL / 2 + Math.round((rng.float() - 0.5) * 2 * Math.max(0, jitter));
      s = { cx, cy, x, y, radius, core: size === 'village' ? 0 : radius * (size === 'city' ? 0.4 : 0.3), name: this.makeName(rng), size, style: this.styles.length ? rng.pick(this.styles) : '' };
    }
    this.rawCache.set(k, s);
    return s;
  }

  private makeName(rng: Rng): string {
    if (!this.lists.length) return `Town ${rng.int(1, 999)}`;
    const list = rng.weighted(this.lists, (l) => l.weight ?? 1)!;
    const whole = list.whole ?? [], pre = list.prefixes ?? [], suf = list.suffixes ?? [];
    if (whole.length && (rng.chance(0.3) || !pre.length || !suf.length)) return rng.pick(whole);
    if (pre.length && suf.length) return rng.pick(pre) + rng.pick(suf);
    return whole.length ? rng.pick(whole) : list.name;
  }

  /** Settlements whose influence may reach (x,y): the 3×3 cells around it. */
  near(x: number, y: number): Settlement[] {
    const cx = Math.floor(x / CELL), cy = Math.floor(y / CELL);
    const out: Settlement[] = [];
    for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) { const s = this.at(cx + i, cy + j); if (s) out.push(s); }
    return out;
  }

  /** Urban density 0..1 from the nearest town (radial falloff, slightly irregular). */
  density(x: number, y: number, warp = 1): number {
    let best = 0, bestS: Settlement | undefined;
    for (const s of this.near(x, y)) {
      const d = Math.hypot(x - s.x, y - s.y) * warp;
      const v = 1 - d / s.radius;
      if (v > best) { best = v; bestS = s; }
    }
    if (!bestS) {
      // outskirts: a thin band of countryside roads/farms around the rim, then nothing
      let v = 0;
      for (const s of this.near(x, y)) { const d = Math.hypot(x - s.x, y - s.y) * warp; if (d < s.radius * 1.6) v = Math.max(v, 0.08 * (1 - (d - s.radius) / (0.6 * s.radius))); }
      return v;
    }
    // shape: 0..0.35 suburb ring → 0.35..1 rising to the core
    const d = Math.hypot(x - bestS.x, y - bestS.y) * warp;
    if (bestS.core > 0 && d < bestS.core) return 0.72 + 0.28 * (1 - d / bestS.core);
    const t = bestS.core > 0 ? (d - bestS.core) / (bestS.radius - bestS.core) : d / bestS.radius; // 0 at core edge → 1 at rim
    return bestS.size === 'village' ? 0.36 + 0.2 * (1 - t) : 0.36 + 0.34 * (1 - t);
  }

  /** The settlement whose radius (×1.6, for outskirts) contains (x,y), nearest first. */
  in(x: number, y: number): Settlement | undefined {
    let best: Settlement | undefined, bd = Infinity;
    for (const s of this.near(x, y)) { const d = Math.hypot(x - s.x, y - s.y) / s.radius; if (d < 1.6 && d < bd) { best = s; bd = d; } }
    return best;
  }

  /** Highway corridors touching a 3×3 cell neighbourhood. Each town links to its east and south neighbours (nearest existing within 2 cells) with an L. */
  corridorsNear(x: number, y: number): Corridor[] {
    const cx = Math.floor(x / CELL), cy = Math.floor(y / CELL);
    const k = `${cx},${cy}`;
    let list = this.corridorCache.get(k);
    if (list) return list;
    list = [];
    const snap = (v: number) => Math.round(v / 128) * 128;
    for (let j = -3; j <= 3; j++) for (let i = -3; i <= 3; i++) { // (±3: a rail leg runs on past a town two cells from its partner)
      const a = this.at(cx + i, cy + j);
      if (!a) continue;
      for (const [di, dj] of [[1, 0], [0, 1]] as const) {
        let b: Settlement | null = null;
        for (let step = 1; step <= 2 && !b; step++) b = this.at(a.cx + di * step, a.cy + dj * step);
        if (!b) continue;
        const ax = snap(a.x), ay = snap(a.y), bx = snap(b.x), by = snap(b.y);
        // L: horizontal at a.y from a.x to b.x, then vertical at b.x from a.y to b.y (hash picks which corner)
        const flip = hash01(this.seed + 17, a.cx, a.cy, b.cx, b.cy) < 0.5;
        const seg = (axis: 'v' | 'h', pos: number, p0: number, p1: number, town: Settlement): Corridor => ({ axis, pos, from: Math.min(p0, p1), to: Math.max(p0, p1), lo: p0 <= p1 ? a : b, hi: p0 <= p1 ? b : a, ends: p0 <= p1 ? [town, null] : [null, town] }); // (p0 is the town's own end of the leg)
        if (flip) { list.push(seg('h', ay, ax, bx, a)); list.push(seg('v', bx, by, ay, b)); }
        else { list.push(seg('v', ax, ay, by, a)); list.push(seg('h', by, bx, ax, b)); }
      }
    }
    this.corridorCache.set(k, list);
    return list;
  }

  /** A railway runs 64 tiles beside about half the corridors (hashed per town pair, side hashed too) — the same L, shifted, both legs
   *  stretched by 64 at the ends so the corner meets. Returns the rail's perpendicular position for this corridor leg, or null. */
  railOf(c: Corridor): number | null {
    const first = c.lo.cx < c.hi.cx || (c.lo.cx === c.hi.cx && c.lo.cy <= c.hi.cy) ? c.lo : c.hi, second = first === c.lo ? c.hi : c.lo; // (the two legs of one L list the towns in either order)
    const h = hash01(this.seed + 23, first.cx, first.cy, second.cx, second.cy);
    if (h < 0.5) return null;
    return c.pos + (h < 0.75 ? 64 : -64);
  }
  /** Is the level-1 line (axis,pos) in segment seg (128-tile) a railway? A leg runs from one segment past the corner (so the two
   *  legs of the L always cross — the rail sits 64 off the corridor, half a segment) right through its town and out past the rim
   *  (`railEnd`), so the line's dead end — where a train dwells and turns — is in the country, not on a downtown avenue (owner, Sep 7). */
  onRail(axis: 'v' | 'h', pos: number, seg: number): boolean { return this.railSide(axis, pos, seg) !== 0; }
  /**
   * The railway on the line (axis,pos) in segment seg, and which side of that line the path world lays the track on:
   * +1 / −1 (half a cell out that way — Q82 b: the line keeps its road, the railway runs beside it, mid-block), 0 for
   * no railway here. The side is the line's, hashed, not the corridor's: two legs from two corridors can share a
   * line end to end (seed 7, column 7), and a train cannot take the jump a side change would make between them.
   */
  railSide(axis: 'v' | 'h', pos: number, seg: number): -1 | 0 | 1 {
    const u0 = seg * 128, u1 = u0 + 128;
    const [x, y] = axis === 'v' ? [pos, u0 + 64] : [u0 + 64, pos];
    for (const c of this.corridorsNear(x, y)) { if (c.axis !== axis) continue; const rp = this.railOf(c); if (rp === pos && c.from - railEnd(c.ends[0]) <= u0 && c.to + railEnd(c.ends[1]) >= u1) return hash01(this.seed + 29, axis === 'v' ? 1 : 2, pos) < 0.5 ? 1 : -1; }
    return 0;
  }
  /** Is the level-0 line (axis,pos) in segment seg part of a highway corridor? */
  onCorridor(axis: 'v' | 'h', pos: number, seg: number): boolean {
    const u0 = seg * 128, u1 = u0 + 128;
    const [x, y] = axis === 'v' ? [pos, u0 + 64] : [u0 + 64, pos];
    for (const c of this.corridorsNear(x, y)) if (c.axis === axis && c.pos === pos && c.from <= u0 && c.to >= u1) return true;
    return false;
  }
}
